import { defineStore } from 'pinia'

const SESSION_HINT_KEY = 'student_session_hint'

interface StudentProfile {
  contact_token: string
  contact_fullname?: string
  contact_email?: string
}

export const useStudentStore = defineStore('student', () => {
  const profile = ref<StudentProfile | null>(null)
  const loading = ref(false)
  const initialized = ref(false)

  const isLoggedIn = computed(() => !!profile.value)
  const displayName = computed(() =>
    profile.value?.contact_fullname
    || profile.value?.contact_email?.split('@')[0]
    || ''
  )
  const initials = computed(() => {
    const name = profile.value?.contact_fullname ?? ''
    return name.split(' ').slice(0, 2).map(w => w[0]?.toUpperCase() ?? '').join('') || '?'
  })

  function getApiBase(): string {
    if (import.meta.dev) return "/_api"
    const config = useRuntimeConfig()
    return (config.public.NUXT_APP_LANDING_API as string) || 'https://v2-api.bibleawr.com'
  }

  function hasSessionHint(): boolean {
    if (!import.meta.client) return false
    return !!localStorage.getItem(SESSION_HINT_KEY)
  }

  function setSessionHint() {
    if (import.meta.client) localStorage.setItem(SESSION_HINT_KEY, '1')
  }

  function clearSessionHint() {
    if (import.meta.client) localStorage.removeItem(SESSION_HINT_KEY)
  }

  async function fetchProfile() {
    if (initialized.value) return
    // No session hint → skip network request, show "login" button immediately
    if (!hasSessionHint()) {
      initialized.value = true
      return
    }
    loading.value = true
    try {
      const apiBase = getApiBase()
      const res = await fetch(`${apiBase}/my/profile`, { credentials: 'include' })
      if (res.ok) {
        const body = await res.json().catch(() => null)
        profile.value = body?.data ?? body ?? null
        setSessionHint()
      } else {
        profile.value = null
        clearSessionHint()
      }
    } catch {
      profile.value = null
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  // Used on /my/* pages — always checks session regardless of hint or initialized state
  async function checkAuth() {
    if (loading.value) return
    loading.value = true
    try {
      const apiBase = getApiBase()
      const res = await fetch(`${apiBase}/my/profile`, { credentials: 'include' })
      if (res.ok) {
        const body = await res.json().catch(() => null)
        profile.value = body?.data ?? body ?? null
        setSessionHint()
      } else {
        profile.value = null
        clearSessionHint()
      }
    } catch {
      profile.value = null
    } finally {
      loading.value = false
      initialized.value = true
    }
  }

  async function logout() {
    const apiBase = getApiBase()
    await fetch(`${apiBase}/my/auth/logout`, { method: 'POST', credentials: 'include' })
    profile.value = null
    initialized.value = true
    clearSessionHint()
  }

  function markLoggedIn() {
    setSessionHint()
    profile.value = null
    initialized.value = false
  }

  function reset() {
    profile.value = null
    initialized.value = true
    clearSessionHint()
  }

  return { profile, loading, initialized, isLoggedIn, displayName, initials, fetchProfile, checkAuth, logout, markLoggedIn, reset }
})
